import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactElement } from 'react';
import { useAuth } from './auth/AuthContext';
import { AppShell } from './layouts/AppShell';
import { PublicLayout } from './layouts/PublicLayout';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Catalog } from './pages/Catalog';
import { CourseDetail } from './pages/CourseDetail';
import { Dashboard } from './pages/Dashboard';
import { MyLearning } from './pages/MyLearning';
import { Player } from './pages/Player';
import { OrgDashboard } from './pages/org/OrgDashboard';
import { OrgEmployees } from './pages/org/OrgEmployees';
import { OrgAssignments } from './pages/org/OrgAssignments';
import { Users } from './pages/Users';
import { Roles } from './pages/Roles';
import { Notifications } from './pages/Notifications';
import { NotificationTemplates } from './pages/admin/NotificationTemplates';
import { AiKnowledgeBase } from './pages/admin/AiKnowledgeBase';
import { Reports } from './pages/admin/Reports';
import { SiteContent } from './pages/admin/SiteContent';
import { Profile } from './pages/Profile';
import { Certificates } from './pages/Certificates';
import { Customers } from './pages/admin/Customers';
import { AdminCourses } from './pages/admin/AdminCourses';
import { CourseWizard } from './pages/admin/CourseWizard';
import { CourseBuilder } from './pages/admin/CourseBuilder';
import { ContentLibrary } from './pages/admin/ContentLibrary';
import { ReviewQueue } from './pages/admin/ReviewQueue';
import { Checkout } from './pages/Checkout';
import { PromoCodes } from './pages/admin/PromoCodes';
import { SitePage } from './pages/SitePage';
import { Faq } from './pages/Faq';
import { VerifyCertificate } from './pages/VerifyCertificate';

export function App() {
  return (
    <Routes>
      {/* Anonymous catalog. Open to everyone, signed in or not. */}
      <Route element={<PublicLayout />}>
        <Route path="/catalog" element={<Catalog />} />
        <Route path="/courses/:slug" element={<CourseDetail />} />
        {/* Where every certificate's QR code and printed link point. Public,
            because the person checking a credential is the one party
            guaranteed not to have an account here. */}
        <Route path="/verify" element={<VerifyCertificate />} />
        <Route path="/verify/:certificateNo" element={<VerifyCertificate />} />
        {/* Site Content: published pages (privacy, terms, about…) and FAQs. */}
        <Route path="/pages/:slug" element={<SitePage />} />
        <Route path="/faq" element={<Faq />} />
      </Route>

      <Route
        path="/login"
        element={
          <AnonymousOnly>
            <Login />
          </AnonymousOnly>
        }
      />
      <Route
        path="/register"
        element={
          <AnonymousOnly>
            <Register />
          </AnonymousOnly>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <AnonymousOnly>
            <ForgotPassword />
          </AnonymousOnly>
        }
      />
      <Route
        path="/reset-password"
        element={
          <AnonymousOnly>
            <ResetPassword />
          </AnonymousOnly>
        }
      />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/my/courses" element={<MyLearning />} />
        <Route path="/learn/:courseId" element={<Player />} />
        <Route path="/my/certificates" element={<Certificates />} />
        <Route path="/checkout/:slug" element={<Checkout />} />
        <Route path="/admin/promo-codes" element={<PromoCodes />} />
        <Route path="/org" element={<OrgDashboard />} />
        <Route path="/org/employees" element={<OrgEmployees />} />
        <Route path="/org/assignments" element={<OrgAssignments />} />
        <Route path="/admin/courses" element={<AdminCourses />} />
        {/* Before :id, or "new" would be read as a course id. */}
        <Route path="/admin/courses/new" element={<CourseWizard />} />
        <Route path="/admin/courses/:id" element={<CourseBuilder />} />
        <Route path="/admin/content" element={<ContentLibrary />} />
        <Route path="/admin/review" element={<ReviewQueue />} />
        <Route path="/admin/users" element={<Users />} />
        <Route path="/admin/customers" element={<Customers />} />
        <Route path="/admin/roles" element={<Roles />} />
        <Route path="/my/notifications" element={<Notifications />} />
        <Route path="/admin/notifications" element={<NotificationTemplates />} />
        <Route path="/admin/ai" element={<AiKnowledgeBase />} />
        <Route path="/admin/reports" element={<Reports />} />
        <Route path="/admin/site" element={<SiteContent />} />
        <Route path="/profile" element={<Profile />} />
      </Route>

      <Route path="/" element={<Navigate to="/catalog" replace />} />
      <Route path="*" element={<Navigate to="/catalog" replace />} />
    </Routes>
  );
}

function RequireAuth({ children }: { children: ReactElement }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div className="spinner">Loading…</div>;
  // The route guard is convenience only — the API enforces access regardless.
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

function AnonymousOnly({ children }: { children: ReactElement }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="spinner">Loading…</div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}
